/**
 * Onboarding Context
 * Multi-step guided onboarding for new users with user-specific persistence
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';

export type OnboardingAction = 
  | 'none'
  | 'complete-profile'
  | 'create-event'
  | 'create-tour'
  | 'add-equipment'
  | 'explore-features';

interface OnboardingContextType {
  isActive: boolean;
  currentStep: number;
  pendingAction: OnboardingAction;
  completeStep: () => void;
  skip: () => void;
  restart: () => void;
  clearAction: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const STORAGE_PREFIX = 'me-platform-onboarding';

function getStorageKey(userId: string) {
  return `${STORAGE_PREFIX}-${userId}`;
}

const STEP_ACTIONS: OnboardingAction[] = [
  'none',                // welcome
  'complete-profile',    // complete profile
  'create-event',        // create first event
  'create-tour',         // create first tour
  'add-equipment',       // add equipment
  'explore-features',    // explore
];

const STEP_ROUTES = [
  '/dashboard',      // welcome → dashboard
  '/settings/profile', // complete-profile → settings
  '/events',          // create-event → events
  '/tours',           // create-tour → tours
  '/equipment',       // add-equipment → equipment
  '/dashboard',       // explore → dashboard
];

interface StoredState {
  isActive: boolean;
  currentStep: number;
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const storageKey = userId ? getStorageKey(userId) : null;

  const [isActive, setIsActive] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [pendingAction, setPendingAction] = useState<OnboardingAction>('none');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load state from localStorage on mount (user-specific)
  useEffect(() => {
    if (!storageKey) {
      setIsLoaded(true);
      return;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as StoredState;
        setIsActive(parsed.isActive);
        setCurrentStep(parsed.currentStep || 0);
      }
    } catch (e) {
      console.error('Failed to load onboarding state:', e);
    }
    setIsLoaded(true);
  }, [storageKey]);

  // Save state to localStorage (user-specific)
  const saveState = useCallback((newState: StoredState) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(newState));
    } catch (e) {
      console.error('Failed to save onboarding state:', e);
    }
  }, [storageKey]);

  const clearAction = useCallback(() => {
    setPendingAction('none');
  }, []);

  const completeStep = useCallback(() => {
    const nextStep = currentStep + 1;
    if (nextStep >= STEP_ACTIONS.length) {
      setIsActive(false);
      setPendingAction('none');
      saveState({ isActive: false, currentStep: 0 });
    } else {
      setCurrentStep(nextStep);
      // Set the action for the NEW step we're going to
      const newAction = STEP_ACTIONS[nextStep] || 'none';
      setPendingAction(newAction);
      saveState({ isActive: true, currentStep: nextStep });
    }
  }, [currentStep, saveState]);

  const skip = useCallback(() => {
    setIsActive(false);
    setPendingAction('none');
    saveState({ isActive: false, currentStep });
  }, [currentStep, saveState]);

  const restart = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    setPendingAction(STEP_ACTIONS[1]); // Start with create-event action
    saveState({ isActive: true, currentStep: 0 });
  }, [saveState]);

  // Set initial action when onboarding becomes active
  useEffect(() => {
    if (isLoaded && isActive && pendingAction === 'none') {
      // Set action for current step
      const action = STEP_ACTIONS[currentStep] || 'none';
      if (action !== 'none') {
        setPendingAction(action);
      }
    }
  }, [isLoaded, isActive, currentStep, pendingAction]);

  return (
    <OnboardingContext.Provider
      value={{
        isActive: isLoaded && isActive,
        currentStep,
        pendingAction,
        completeStep,
        skip,
        restart,
        clearAction,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

// Export step data for the modal
export const ONBOARDING_STEPS = [
  { id: 'welcome', route: STEP_ROUTES[0] },
  { id: 'complete-profile', route: STEP_ROUTES[1] },
  { id: 'create-event', route: STEP_ROUTES[2] },
  { id: 'create-tour', route: STEP_ROUTES[3] },
  { id: 'add-equipment', route: STEP_ROUTES[4] },
  { id: 'explore-features', route: STEP_ROUTES[5] },
];